import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchCourseById,
  clearSelectedEntity,
  selectSelectedCourse,
  selectCourseDetailStatus,
  selectCourseDetailError,
} from '../../features/courses/courseSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import CategoryItems from '../../components/reusable/CategoryItems';
import InstructorItems from '../../components/reusable/InstructorItems';
import courseFields from '../../config/courseFields';
import { showToast, hasPermission } from '../../utils/utils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft, FiUsers, FiDollarSign, FiBook, FiVideo } from 'react-icons/fi';

/**
 * Preview Course Page
 * Full page read-only view of course details
 */
const PreviewCoursePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedCourse = useSelector(selectSelectedCourse);
  const detailStatus = useSelector(selectCourseDetailStatus);
  const detailError = useSelector(selectCourseDetailError);

  // Handle navigation to related data pages
  const navigateToRelatedPage = (page) => {
    const courseState = { course: selectedCourse };

    switch (page) {
      case 'students':
        navigate(`/dashboard/courses/${id}/student`, { state: courseState });
        break;
      case 'payments':
        navigate(`/dashboard/courses/${id}/payment`, { state: courseState });
        break;
      case 'materials':
        navigate(`/dashboard/courses/${id}/material`, { state: courseState });
        break;
      case 'lessons':
        navigate(`/dashboard/courses/lesson/${id}`, { state: courseState });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchCourseById({ token: user.token, courseId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/courses', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/courses')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Courses
          </button>
        </div>
      </div>
    );
  }

  if (!selectedCourse) {
    return null;
  }

  const textFields = courseFields.filter(field =>
    (field.type === 'text' || field.type === 'number') &&
    field.name !== 'category_id' &&
    field.name !== 'instructor_id'
  );
  const textareaField = courseFields.find(field => field.type === 'textarea');
  const toggleField = courseFields.find(field => field.type === 'toggle');
  const fileField = courseFields.find(field => field.type === 'file');
  const categoryField = courseFields.find(field => field.name === 'category_id');
  const instructorField = courseFields.find(field => field.name === 'instructor_id');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  const imageVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" }
    }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/courses"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Course Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View course information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/courses/${id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit Course</span>
            </Link>
          </div>

        </motion.div>

        {/* Quick Action Cards - Course Related Data */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-6"
        >
          {/* Students Card */}
          {hasPermission(user, 'view_student') && (
            <motion.div
              onClick={() => navigateToRelatedPage('students')}
              className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-xs sm:text-sm font-medium">Students</p>
                  <p className="text-white text-lg sm:text-2xl font-bold mt-1">
                    {selectedCourse?.students_count}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-2 sm:p-3 group-hover:bg-white/30 transition-colors">
                  <FiUsers className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-blue-100 text-xs mt-2 group-hover:text-white transition-colors">
                View all enrolled students →
              </p>
            </motion.div>
          )}

          {/* Payments Card */}
          {hasPermission(user, 'view_payment') && (
            <motion.div
              onClick={() => navigateToRelatedPage('payments')}
              className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-xs sm:text-sm font-medium">Payments</p>
                  <p className="text-white text-lg sm:text-2xl font-bold mt-1">
                    {selectedCourse?.payments_count}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-2 sm:p-3 group-hover:bg-white/30 transition-colors">
                  <FiDollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-green-100 text-xs mt-2 group-hover:text-white transition-colors">
                View payment history →
              </p>
            </motion.div>
          )}

          {/* Materials Card */}
          {hasPermission(user, 'view_material') && (
            <motion.div
              onClick={() => navigateToRelatedPage('materials')}
              className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-xs sm:text-sm font-medium">Materials</p>
                  <p className="text-white text-lg sm:text-2xl font-bold mt-1">
                    {selectedCourse?.materials_count}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-2 sm:p-3 group-hover:bg-white/30 transition-colors">
                  <FiBook className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-purple-100 text-xs mt-2 group-hover:text-white transition-colors">
                View course materials →
              </p>
            </motion.div>
          )}

          {/* Lessons Card */}
          {hasPermission(user, 'view_lesson') && (
            <motion.div
              onClick={() => navigateToRelatedPage('lessons')}
              className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 cursor-pointer shadow-lg hover:shadow-xl transition-all duration-300 group"
              whileHover={{ scale: 1.02, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-xs sm:text-sm font-medium">Lessons</p>
                  <p className="text-white text-lg sm:text-2xl font-bold mt-1">
                    {selectedCourse?.lessons_count}
                  </p>
                </div>
                <div className="bg-white/20 rounded-full p-2 sm:p-3 group-hover:bg-white/30 transition-colors">
                  <FiVideo className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                </div>
              </div>
              <p className="text-orange-100 text-xs mt-2 group-hover:text-white transition-colors">
                View all lessons →
              </p>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {textFields.map((field, index) => (
              <motion.div
                key={field.name}
                variants={itemVariants}
                transition={{ delay: index * 0.05 }}
              >
                <InputField
                  label={field.label}
                  name={field.name}
                  placeholder={field.placeholder}
                  value={selectedCourse[field.name] || ''}
                  disabled={true}
                />
              </motion.div>
            ))}

            {categoryField && (
              <motion.div variants={itemVariants}>
                <CategoryItems
                  field={categoryField}
                  formData={selectedCourse}
                  type="preview"
                  disabled={true}
                />
              </motion.div>
            )}

            {instructorField && (
              <motion.div variants={itemVariants}>
                <InstructorItems
                  field={instructorField}
                  formData={selectedCourse}
                  type="preview"
                  disabled={true}
                />
              </motion.div>
            )}

            {toggleField && (
              <motion.div
                key={toggleField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ToggleSwitch
                  label={toggleField.label}
                  checked={selectedCourse[toggleField.name] === 'active' || !!selectedCourse[toggleField.name]}
                  className="space-x-2"
                  disabled={true}
                />
              </motion.div>
            )}

            {textareaField && (
              <motion.div
                key={textareaField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="sm:col-span-2"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {textareaField.label}
                </label>
                <textarea
                  name={textareaField.name}
                  className="w-full h-32 p-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed resize-none overflow-y-auto dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder={textareaField.placeholder}
                  value={selectedCourse[textareaField.name] || ''}
                  disabled={true}
                />
              </motion.div>
            )}
          </div>

          {fileField && selectedCourse[fileField.name] && (
            <motion.div
              className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700"
              variants={imageVariants}
            >
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {fileField.label}
              </label>
              <div className="flex justify-center sm:justify-start">
                <img
                  className="w-full sm:w-auto sm:max-w-md h-auto max-h-96 object-cover rounded-lg shadow-lg"
                  src={selectedCourse[fileField.name]}
                  alt="Course"
                />
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewCoursePage;
